'use client';

// The one way into the payment modal from app chrome.
//
// Before this, an upgrade could only be reached by finishing the guided
// onboarding or by tripping a gate (pool exhausted, a premium feature). Anyone
// who wanted to pay before they ran out of free runs had nowhere to click. It
// now sits in all three headers — LandingNav, HomeHeader, TopBar — which is
// every surface the app renders a header on. The one place it deliberately
// does NOT appear is guided onboarding, where TopBar is collapsed to zero
// height by RoomShell.
//
// Shape: `secondary` off the button ladder, i.e. the primary pill with the
// fill removed. Each header already spends its one ink fill on its own primary
// (Design your room / New room / Save), and the accent is reserved for
// position-in-flow (AGENTS.md), so a bordered pill is the only tier left that
// is still a real action. It sits immediately before the primary in the right
// cluster, per the header grammar: secondary action(s) → primary → account.
//
// Below `sm` the label is dropped for the shared header dot — the same
// pill-at-sm / dot-below trade TopBar's own Share and Sign-in controls make,
// because three labelled pills do not fit a 375px bar. The affordance is
// present at every width; only its form changes.
//
// The marketing header is the exception to the shape (owner, 2026-09-26: the
// header had "way too many things crumpled up in it"): there it is a nav
// entry, "Pricing", among the header's few links (`appearance="nav"`), and on
// a phone a row in the menu drawer (`appearance="menu"`) rather than a fourth
// control in the bar. Still present at every width; only its form changes.

import { ArrowUp } from 'lucide-react';
import { buttonClass } from '@/components/ui/Button';
import { CHROME_DOT } from '@/components/ui/chromeDot';
import { PillButton } from '@/components/site/PillLink';
import { useUpgradeModal } from './UpgradeModalProvider';
import { useNativeHost } from '@/components/room/state/NativeHostContext';
import type { Trigger } from '@/lib/analytics';

export default function UpgradeButton({
  /** Nothing left to sell here (unlimited rooms, or the editor's room is
   *  premium already), so render nothing. Resolved on the server by the
   *  marketing and dashboard headers (no extra /api/auth/me round trip on
   *  anonymous marketing traffic). */
  isPro = false,
  /** Which header opened it, for the per-surface conversion funnel. */
  trigger = 'header_upgrade',
  /** What opening the plans means here, when it is more than the plain
   *  modal: the editor opens them for its own room (useRoomTier). */
  onOpen,
  appearance = 'pill',
  className = '',
}: {
  isPro?: boolean;
  trigger?: Trigger;
  onOpen?: () => void;
  /** `pill` (app headers: labelled pill, header dot below `sm`), `nav` (the
   *  marketing header's "Pricing" entry) or `menu` (a row in its drawer). */
  appearance?: 'pill' | 'nav' | 'menu';
  /** The row recipe for `menu`, owned by the drawer that lays it out. */
  className?: string;
}) {
  const { open } = useUpgradeModal();
  // Inside the app a plan is only sold where the App Store storefront allows
  // web checkout (`capabilities.checkout`, ADR 009); anywhere else in the app
  // this renders nothing, same gate as UpgradeModalProvider.open.
  const { isNative, capabilities } = useNativeHost();
  if (isPro || (isNative && capabilities.checkout !== 'web')) return null;

  const onClick = onOpen ?? (() => open({ trigger }));

  if (appearance === 'nav') {
    return (
      <span className="inline-flex" data-commerce>
        <PillButton data-testid="upgrade-button" onClick={onClick}>Pricing</PillButton>
      </span>
    );
  }
  if (appearance === 'menu') {
    return (
      <button type="button" data-commerce data-testid="upgrade-button-menu" onClick={onClick} className={className}>
        Pricing
      </button>
    );
  }

  return (
    <>
      {/* Visibility lives on a wrapper, never in the button's own className:
          `buttonClass` starts with `inline-flex`, and Tailwind emits `hidden`
          BEFORE `inline-flex`, so a `hidden` passed through className loses
          the cascade and the control renders at every width. TopBar shipped
          that bug with a pair of share buttons; see its comment. */}
      <span className="hidden sm:inline-flex" data-commerce>
        <button
          type="button"
          data-testid="upgrade-button"
          onClick={onClick}
          className={buttonClass({ variant: 'secondary', size: 'md' })}
        >
          Upgrade
        </button>
      </span>
      <span className="inline-flex sm:hidden" data-commerce>
        <button
          type="button"
          data-testid="upgrade-button-compact"
          onClick={onClick}
          aria-label="Upgrade"
          title="Upgrade"
          className={CHROME_DOT}
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      </span>
    </>
  );
}
